import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '@/services/api';

interface AuthState {
  authenticated: boolean | null; // null = still checking
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .get<{ authenticated: boolean }>('/auth/me')
      .then((r) => alive && setAuthenticated(r.authenticated))
      .catch(() => alive && setAuthenticated(false));
    return () => {
      alive = false;
    };
  }, []);

  const login = async (password: string) => {
    await api.post('/auth/login', { password });
    setAuthenticated(true);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    setAuthenticated(false);
  };

  return (
    <AuthCtx.Provider value={{ authenticated, login, logout }}>{children}</AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
